import React from 'react';
import { CardBody } from "@backstage/ui";
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { makeStyles } from '@material-ui/core';
import { Amplify } from 'aws-amplify';
import { createStorageBrowser, StorageBrowserValue } from '@aws-amplify/ui-react-storage/browser';

import { SideGridItem, SideFooter } from './page';
import { pagesTranslationRef } from '../../translations/pages';
import AreaPage from './page';

import '@aws-amplify/ui-react-storage/styles.css';
import './s3-style.css';


Amplify.configure({});


const Empty = () => ( <></> );


const useStyles = makeStyles(() => ({
    content: {
        '& div.amplify-storage-browser__text-preview': {
            color: 'black !important',
        },
    },
    s3footer: {
        paddingBottom: '0.5rem',
    },
    s3keyslink: {
        margin: '0 0 0.5rem 0',
    },
    body: {
        padding: 0,
    },
}));


const S3KeysLink = () => {
    const classes = useStyles();
    const configApi = useApi(configApiRef);
    const {t} = useTranslationRef(pagesTranslationRef);
    const accessKey = configApi.getOptionalString('s3.accessKey') || '';
    const secretKey = configApi.getOptionalString('s3.secretKey') || '';
    const content = [`AWS_ACCESS_KEY_ID=${accessKey}`, `AWS_SECRET_ACCESS_KEY=${secretKey}`].join('\r\n');
    const url = 'data:text/plain;base64,' + btoa(content);
    return (
        <a className={classes.s3keyslink} href={url} download='s3-keys.txt' target='_blank'>
            {t('pages.downloadApiKeys')}
        </a>
    );
}


const S3BrowserComponent = () => {
    const configApi = useApi(configApiRef);
    const endpoint = configApi.getOptionalString('s3.endpoint');
    const region = configApi.getOptionalString('s3.region') || '';
    const bucket = configApi.getOptionalString('s3.bucket') || '';
    const accessKey = configApi.getOptionalString('s3.accessKey') || '';
    const secretKey = configApi.getOptionalString('s3.secretKey') || '';
    const {t} = useTranslationRef(pagesTranslationRef);

    const dictionary = {
        CopyView: {
            actionCancelLabel: t('pages.objectBrowser.cancelLabel'),
            actionExitLabel: t('pages.objectBrowser.exitLabel'),
            actionDestinationLabel: t('pages.objectBrowser.copy.destinationLabel'),
            statusDisplayCanceledLabel: t('pages.objectBrowser.statusDisplayCanceledLabel'),
            statusDisplayCompletedLabel: t('pages.objectBrowser.statusDisplayCompletedLabel'),
            statusDisplayFailedLabel: t('pages.objectBrowser.statusDisplayFailedLabel'),
            statusDisplayInProgressLabel: t('pages.objectBrowser.statusDisplayInProgressLabel'),
            statusDisplayTotalLabel: t('pages.objectBrowser.statusDisplayTotalLabel'),
            statusDisplayQueuedLabel: t('pages.objectBrowser.statusDisplayQueuedLabel'),
            tableColumnCancelHeader: t('pages.objectBrowser.tableColumnCancelHeader'),
            tableColumnStatusHeader: t('pages.objectBrowser.tableColumnStatusHeader'),
            tableColumnFolderHeader: t('pages.objectBrowser.tableColumnFolderHeader'),
            tableColumnNameHeader: t('pages.objectBrowser.tableColumnNameHeader'),
            tableColumnTypeHeader: t('pages.objectBrowser.tableColumnTypeHeader'),
            tableColumnSizeHeader: t('pages.objectBrowser.tableColumnSizeHeader'),
            tableColumnProgressHeader: t('pages.objectBrowser.tableColumnProgressHeader'),
            title: t('pages.objectBrowser.copy.title'),
            actionStartLabel: t('pages.objectBrowser.action.copy'),
            // @ts-ignore
            getListFoldersResultsMessage: ({ folders, query, message, hasError, hasExhaustedSearch, }) => {
                if (!folders?.length) {
                    return {
                        content: query
                         ? t('pages.objectBrowser.noFoldersMatching', {query: query})
                         : t('pages.objectBrowser.noSubfoldersFound'),
                        type: 'info',
                    };
                }
                if (message && !!query) {
                    return { content: t('pages.objectBrowser.errorLoadingFolders'), type: 'error' };
                }
                if (hasError) {
                    return { content: t('pages.objectBrowser.errorLoadingFolders'), type: 'error' };
                }
                if (hasExhaustedSearch) {
                    return { content: t('pages.objectBrowser.resultsTruncated'), type: 'info', };
                }
                return undefined;
            },
            loadingIndicatorLabel: t('pages.objectBrowser.loadingIndicatorLabel'),
            overwriteWarningMessage: t('pages.objectBrowser.overwriteWarningMessage'),
            searchPlaceholder: t('pages.objectBrowser.copy.searchPlaceholder'),
            // @ts-ignore
            getActionCompleteMessage: (data) => {
                const { counts } = data ?? {};
                const { COMPLETE, FAILED, TOTAL } = counts ?? {};
                if (COMPLETE === TOTAL) {
                    return { content: t('pages.objectBrowser.allFilesCoped'), type: 'success', };
                }
                if (FAILED === TOTAL) {
                    return { content: t('pages.objectBrowser.allFilesFailedToCopy'), type: 'error' };
                }
                return { content: t('pages.objectBrowser.someFilesFailedToCopy', {complete: COMPLETE, failed: FAILED}), type: 'error', };
            },
            searchSubmitLabel: t('pages.objectBrowser.searchSubmitLabel'),
            searchClearLabel: t('pages.objectBrowser.searchClearLabel'),
        },
        CreateFolderView: {
            actionCancelLabel: t('pages.objectBrowser.cancelLabel'),
            actionExitLabel: t('pages.objectBrowser.exitLabel'),
            actionDestinationLabel: t('pages.objectBrowser.destinationLabel'),
            statusDisplayCanceledLabel: t('pages.objectBrowser.statusDisplayCanceledLabel'),
            statusDisplayCompletedLabel: t('pages.objectBrowser.statusDisplayCompletedLabel'),
            statusDisplayFailedLabel: t('pages.objectBrowser.statusDisplayFailedLabel'),
            statusDisplayInProgressLabel: t('pages.objectBrowser.statusDisplayInProgressLabel'),
            statusDisplayTotalLabel: t('pages.objectBrowser.statusDisplayTotalLabel'),
            statusDisplayQueuedLabel: t('pages.objectBrowser.statusDisplayQueuedLabel'),
            tableColumnCancelHeader: t('pages.objectBrowser.tableColumnCancelHeader'),
            tableColumnStatusHeader: t('pages.objectBrowser.tableColumnStatusHeader'),
            tableColumnFolderHeader: t('pages.objectBrowser.tableColumnFolderHeader'),
            tableColumnNameHeader: t('pages.objectBrowser.tableColumnNameHeader'),
            tableColumnTypeHeader: t('pages.objectBrowser.tableColumnTypeHeader'),
            tableColumnSizeHeader: t('pages.objectBrowser.tableColumnSizeHeader'),
            tableColumnProgressHeader: t('pages.objectBrowser.tableColumnProgressHeader'),
            title: t('pages.objectBrowser.createFolder.title'),
            actionStartLabel: t('pages.objectBrowser.action.createFolder'),
            folderNameLabel: t('pages.objectBrowser.folderNameLabel'),
            folderNamePlaceholder: t('pages.objectBrowser.folderNamePlaceholder'),
            getValidationMessage: () => t('pages.objectBrowser.getValidationMessage'),
            // @ts-ignore
            getActionCompleteMessage: (data) => {
                const { counts } = data ?? {};
                const { FAILED, OVERWRITE_PREVENTED } = counts ?? {};
                if (OVERWRITE_PREVENTED) {
                    return { content: t('pages.objectBrowser.folderAlreadyExists'), type: 'warning', };
                }
                if (FAILED) {
                    return { content: t('pages.objectBrowser.failedCreateFolder'), type: 'error', };
                }
                return { content: t('pages.objectBrowser.folderCreated'), type: 'success' };
            },
        },
        DeleteView: {
            actionCancelLabel: t('pages.objectBrowser.cancelLabel'),
            actionExitLabel: t('pages.objectBrowser.exitLabel'),
            actionDestinationLabel: t('pages.objectBrowser.destinationLabel'),
            statusDisplayCanceledLabel: t('pages.objectBrowser.statusDisplayCanceledLabel'),
            statusDisplayCompletedLabel: t('pages.objectBrowser.statusDisplayCompletedLabel'),
            statusDisplayFailedLabel: t('pages.objectBrowser.statusDisplayFailedLabel'),
            statusDisplayInProgressLabel: t('pages.objectBrowser.statusDisplayInProgressLabel'),
            statusDisplayTotalLabel: t('pages.objectBrowser.statusDisplayTotalLabel'),
            statusDisplayQueuedLabel: t('pages.objectBrowser.statusDisplayQueuedLabel'),
            tableColumnCancelHeader: t('pages.objectBrowser.tableColumnCancelHeader'),
            tableColumnStatusHeader: t('pages.objectBrowser.tableColumnStatusHeader'),
            tableColumnFolderHeader: t('pages.objectBrowser.tableColumnFolderHeader'),
            tableColumnNameHeader: t('pages.objectBrowser.tableColumnNameHeader'),
            tableColumnTypeHeader: t('pages.objectBrowser.tableColumnTypeHeader'),
            tableColumnSizeHeader: t('pages.objectBrowser.tableColumnSizeHeader'),
            tableColumnProgressHeader: t('pages.objectBrowser.tableColumnProgressHeader'),
            title: t('pages.objectBrowser.delete.title'),
            actionStartLabel: t('pages.objectBrowser.action.delete'),
            // @ts-ignore
            getActionCompleteMessage: (data) => {
                const { counts } = data ?? {};
                const { COMPLETE, FAILED, TOTAL } = counts ?? {};
                if (COMPLETE === TOTAL) {
                    return { content: t('pages.objectBrowser.allFilesDeleted'), type: 'success' };
                }
                if (FAILED === TOTAL) {
                    return { content: t('pages.objectBrowser.allFilesFailedToDelete'), type: 'error' };
                }
                return { content: t('pages.objectBrowser.someFilesFailedToDelete', {complete: COMPLETE, failed: FAILED}), type: 'error', };
            },
        },
        DownloadView: {
            actionCancelLabel: t('pages.objectBrowser.cancelLabel'),
            actionExitLabel: t('pages.objectBrowser.exitLabel'),
            actionDestinationLabel: t('pages.objectBrowser.destinationLabel'),
            statusDisplayCanceledLabel: t('pages.objectBrowser.statusDisplayCanceledLabel'),
            statusDisplayCompletedLabel: t('pages.objectBrowser.statusDisplayCompletedLabel'),
            statusDisplayFailedLabel: t('pages.objectBrowser.statusDisplayFailedLabel'),
            statusDisplayInProgressLabel: t('pages.objectBrowser.statusDisplayInProgressLabel'),
            statusDisplayTotalLabel: t('pages.objectBrowser.statusDisplayTotalLabel'),
            statusDisplayQueuedLabel: t('pages.objectBrowser.statusDisplayQueuedLabel'),
            tableColumnCancelHeader: t('pages.objectBrowser.tableColumnCancelHeader'),
            tableColumnStatusHeader: t('pages.objectBrowser.tableColumnStatusHeader'),
            tableColumnFolderHeader: t('pages.objectBrowser.tableColumnFolderHeader'),
            tableColumnNameHeader: t('pages.objectBrowser.tableColumnNameHeader'),
            tableColumnTypeHeader: t('pages.objectBrowser.tableColumnTypeHeader'),
            tableColumnSizeHeader: t('pages.objectBrowser.tableColumnSizeHeader'),
            tableColumnProgressHeader: t('pages.objectBrowser.tableColumnProgressHeader'),
            title: t('pages.objectBrowser.download.title'),
            actionStartLabel: t('pages.objectBrowser.action.download'),
            // @ts-ignore
            getActionCompleteMessage: (data) => {
                const { counts } = data ?? {};
                const { COMPLETE, FAILED, TOTAL } = counts ?? {};
                if (COMPLETE === TOTAL) {
                    return { content: t('pages.objectBrowser.allFilesDownloaded'), type: 'success' };
                }
                if (FAILED === TOTAL) {
                    return { content: t('pages.objectBrowser.allFilesFailedToDownload'), type: 'error' };
                }
                return { content: t('pages.objectBrowser.someFilesFailedToDownload', {complete: COMPLETE, failed: FAILED}), type: 'error', };
            },
        },
        LocationDetailView: {
            loadingIndicatorLabel: t('pages.objectBrowser.loadingIndicatorLabel'),
            searchSubmitLabel: t('pages.objectBrowser.searchSubmitLabel'),
            searchClearLabel: t('pages.objectBrowser.searchClearLabel'),
            // @ts-ignore
            getDateDisplayValue: (date) => new Intl.DateTimeFormat(t('pages.objectBrowser.date.locale'), {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                year: 'numeric',
                minute: 'numeric',
                hourCycle: t('pages.objectBrowser.date.hourCycle'),
            }).format(date),
            // @ts-ignore
            getListItemsResultMessage: (data) => {
                const { items, hasExhaustedSearch, hasError = false, message, isLoading, } = data ?? {};
                if (isLoading) {
                    return undefined;
                }
                if (hasError) {
                    return { type: 'error', content: message ?? t('pages.objectBrowser.locationDetail.defaultErrorMessage'), };
                }
                if (!items?.length && hasExhaustedSearch) {
                    return { type: 'info', content: t('pages.objectBrowser.noResultsButTruncated') };
                }
                if (!items?.length) {
                    return { type: 'info', content: t('pages.objectBrowser.noFiles')};
                }
                if (hasExhaustedSearch) {
                    return { type: 'info', content: t('pages.objectBrowser.resultsTruncated') };
                }
                return undefined;
            },
            searchSubfoldersToggleLabel: t('pages.objectBrowser.searchSubfoldersToggleLabel'),
            searchPlaceholder: t('pages.objectBrowser.locationDetail.searchPlaceholder'),
            tableColumnLastModifiedHeader: t('pages.objectBrowser.tableColumnLastModifiedHeader'),
            tableColumnNameHeader: t('pages.objectBrowser.tableColumnNameHeader'),
            tableColumnSizeHeader: t('pages.objectBrowser.tableColumnSizeHeader'),
            tableColumnTypeHeader: t('pages.objectBrowser.tableColumnTypeHeader'),
            selectFileLabel: t('pages.objectBrowser.selectFileLabel'),
            selectFolderLabel: t('pages.objectBrowser.selectFolderLabel'),
            selectAllFilesLabel: t('pages.objectBrowser.selectAllFilesLabel'),
            getActionListItemLabel: (key = '') => {
                switch (key) {
                    case 'Copy':
                        return t('pages.objectBrowser.action.copy');
                    case 'Delete':
                        return t('pages.objectBrowser.action.delete');
                    case 'Create folder':
                        return t('pages.objectBrowser.action.createFolder');
                    case 'Upload':
                        return t('pages.objectBrowser.action.upload');
                    case 'Download':
                        return t('pages.objectBrowser.action.download');
                    default:
                        return key;
                }
            },
            // getTitle is a (title) => title function, no need to override
            filePreview: {
                closeButtonLabel: t('pages.objectBrowser.filePreview.closeButtonLabel'),
                filePreviewTitle: t('pages.objectBrowser.filePreview.filePreviewTitle'),
                fileInformationTitle: t('pages.objectBrowser.filePreview.fileInformationTitle'),
                errorMessage: t('pages.objectBrowser.filePreview.errorMessage'),
                sizeLimitMessage: t('pages.objectBrowser.filePreview.sizeLimitMessage'),
                unsupportedFileMessage: t('pages.objectBrowser.filePreview.unsupportedFileMessage'),
                keyLabel: t('pages.objectBrowser.filePreview.keyLabel'),
                sizeLabel: t('pages.objectBrowser.filePreview.sizeLabel'),
                versionIdLabel: t('pages.objectBrowser.filePreview.versionIdLabel'),
                lastModifiedLabel: t('pages.objectBrowser.filePreview.lastModifiedLabel'),
                entityTagLabel: t('pages.objectBrowser.filePreview.entityTagLabel'),
                typeLabel: t('pages.objectBrowser.filePreview.typeLabel'),
                unknownValue: t('pages.objectBrowser.filePreview.unknownValue'),
                errorDescription: t('pages.objectBrowser.filePreview.errorDescription'),
                unsupportedFileDescription: t('pages.objectBrowser.filePreview.unsupportedFileDescription'),
                imageLoadErrorDescription: t('pages.objectBrowser.filePreview.imageLoadErrorDescription'),
                videoLoadErrorDescription: t('pages.objectBrowser.filePreview.videoLoadErrorDescription'),
                textLoadErrorDescription: t('pages.objectBrowser.filePreview.textLoadErrorDescription'),
                generalPreviewErrorDescription: t('pages.objectBrowser.filePreview.generalPreviewErrorDescription'),
                fileSizeLimitDescription: t('pages.objectBrowser.filePreview.fileSizeLimitDescription'),
                filePrefix: t('pages.objectBrowser.filePreview.filePrefix'),
                retryButtonLabel: t('pages.objectBrowser.filePreview.retryButtonLabel'),
                downloadButtonLabel: t('pages.objectBrowser.filePreview.downloadButtonLabel'),
                // @ts-ignore
                getTextErrorMessage: (error) => t('pages.objectBrowser.filePreview.getTextErrorMessage', {error: error}),
                emptyFileMessage: t('pages.objectBrowser.filePreview.emptyFileMessage'),
            },
        },
        LocationsView: {
            loadingIndicatorLabel: t('pages.objectBrowser.loadingIndicatorLabel'),
            searchSubmitLabel: t('pages.objectBrowser.searchSubmitLabel'),
            searchClearLabel: t('pages.objectBrowser.searchClearLabel'),
            // @ts-ignore
            getDateDisplayValue: (date) => new Intl.DateTimeFormat(t('pages.objectBrowser.date.locale'), {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                year: 'numeric',
                minute: 'numeric',
                hourCycle: t('pages.objectBrowser.date.hourCycle'),
            }).format(date),
            title: t('pages.objectBrowser.locations.title'),
            searchPlaceholder: t('pages.objectBrowser.locations.searchPlaceholder'),
            // @ts-ignore
            getListLocationsResultMessage: (data) => {
                const { isLoading, items, hasExhaustedSearch, hasError = false, message, } = data ?? {};
                if (isLoading) {
                    return undefined;
                }
                if (hasError) {
                    return { type: 'error', content: message ?? t('pages.objectBrowser.locations.defaultErrorMessage') };
                }
                if (items?.length === 0 && !hasExhaustedSearch) {
                    return { type: 'info', content: t('pages.objectBrowser.noFoldersOrFiles') };
                }
                if (hasExhaustedSearch) {
                    return { type: 'info', content: t('pages.objectBrowser.resultsTruncated') };
                }
                return undefined;
            },
            // @ts-ignore
            getPermissionName: (permissions) => {
                let text = '';
                if (permissions.includes('get') || permissions.includes('list')) {
                    text = t('pages.objectBrowser.permissions.read');
                }
                if (permissions.includes('write') || permissions.includes('delete')) {
                    text = text
                        ? t('pages.objectBrowser.permissions.readWrite')
                        : t('pages.objectBrowser.permissions.write');
                }
                if (!text) {
                    text = permissions.join('/');
                }
                return text;
            },
            // @ts-ignore
            getDownloadLabel: (fileName) => t('pages.objectBrowser.downloadFile', {filename: fileName}),
            tableColumnBucketHeader: t('pages.objectBrowser.tableColumnBucketHeader'),
            tableColumnFolderHeader: t('pages.objectBrowser.tableColumnFolderHeader'),
            tableColumnPermissionsHeader: t('pages.objectBrowser.tableColumnPermissionsHeader'),
            tableColumnActionsHeader: t('pages.objectBrowser.tableColumnActionsHeader'),
        },
        UploadView: {
            actionCancelLabel: t('pages.objectBrowser.cancelLabel'),
            actionExitLabel: t('pages.objectBrowser.exitLabel'),
            actionDestinationLabel: t('pages.objectBrowser.destinationLabel'),
            statusDisplayCanceledLabel: t('pages.objectBrowser.statusDisplayCanceledLabel'),
            statusDisplayCompletedLabel: t('pages.objectBrowser.statusDisplayCompletedLabel'),
            statusDisplayFailedLabel: t('pages.objectBrowser.statusDisplayFailedLabel'),
            statusDisplayInProgressLabel: t('pages.objectBrowser.statusDisplayInProgressLabel'),
            statusDisplayTotalLabel: t('pages.objectBrowser.statusDisplayTotalLabel'),
            statusDisplayQueuedLabel: t('pages.objectBrowser.statusDisplayQueuedLabel'),
            tableColumnCancelHeader: t('pages.objectBrowser.tableColumnCancelHeader'),
            tableColumnStatusHeader: t('pages.objectBrowser.tableColumnStatusHeader'),
            tableColumnFolderHeader: t('pages.objectBrowser.tableColumnFolderHeader'),
            tableColumnNameHeader: t('pages.objectBrowser.tableColumnNameHeader'),
            tableColumnTypeHeader: t('pages.objectBrowser.tableColumnTypeHeader'),
            tableColumnSizeHeader: t('pages.objectBrowser.tableColumnSizeHeader'),
            tableColumnProgressHeader: t('pages.objectBrowser.tableColumnProgressHeader'),
            actionStartLabel: t('pages.objectBrowser.action.upload'),
            addFilesLabel: t('pages.objectBrowser.addFilesLabel'),
            addFolderLabel: t('pages.objectBrowser.addFolderLabel'),
            // @ts-ignore
            getActionCompleteMessage: (data) => {
                const { counts } = data ?? {};
                const { COMPLETE, FAILED, OVERWRITE_PREVENTED, CANCELED } = counts ?? {};
                const hasPreventedOverwrite = !!OVERWRITE_PREVENTED;
                const hasFailure = !!FAILED;
                const hasSuccess = !!COMPLETE;
                const hasCanceled = !!CANCELED;
                const type = hasFailure
                    ? 'error'
                    : hasPreventedOverwrite || hasCanceled
                    ? 'warning'
                    : 'success';
                const preventedOverwriteMessage = hasPreventedOverwrite
                    ? t('pages.objectBrowser.upload.overwritePrevented', {count: OVERWRITE_PREVENTED})
                    : undefined;
                const canceledMessage = hasCanceled
                    ? t('pages.objectBrowser.upload.canceled', {count: CANCELED})
                    : undefined;
                const failedMessage = hasFailure
                    ? t('pages.objectBrowser.upload.failed', {count: FAILED})
                    : undefined;
                const completedMessage = hasSuccess
                    ? t('pages.objectBrowser.upload.uploaded', {count: COMPLETE})
                    : undefined;
                const messages = [
                    preventedOverwriteMessage,
                    failedMessage,
                    canceledMessage,
                    completedMessage,
                ].filter(Boolean);
                if (messages.length > 0) {
                    return { content: messages.join(', ') + '.', type, };
                }
                return { content: t('pages.objectBrowser.allFilesUploaded'), type };
            },
            // @ts-ignore
            getFilesValidationMessage: (data) => {
                if (!data?.invalidFiles) {
                    return undefined;
                }
                const invalidFileNames = data.invalidFiles
                    // @ts-ignore
                    .map(({ file }) => file.name)
                    .join(', ');
                if (invalidFileNames) {
                    return { content: t('pages.objectBrowser.filesTooBig', {filenames: invalidFileNames}), type: 'warning' };
                }
                return undefined;
            },
            overwriteToggleLabel: t('pages.objectBrowser.overwriteToggleLabel'),
            statusDisplayOverwritePreventedLabel: t('pages.objectBrowser.statusDisplayOverwritePreventedLabel'),
            title: t('pages.objectBrowser.upload.title'),
        },
    }

    const { StorageBrowser } = !endpoint ? {StorageBrowser: Empty} : createStorageBrowser({
        config: {
            region: region,
            customEndpoint: endpoint,
            listLocations: async () => {
                return {
                    items: [
                        {
                            bucket: bucket,
                            prefix: '', // empty path means bucket root
                            id: `id-${bucket}`, // unique identifier
                            type: 'BUCKET',
                            permissions: ['delete', 'get', 'list', 'write'],
                        },
                    ],
                    nextToken: '',
                }
            },
            getLocationCredentials: async ({}) => {
                // get credentials for specified scope and permission
                return {
                    credentials: {
                        accessKeyId: accessKey,
                        secretAccessKey: secretKey,
                        sessionToken: '',
                        expiration: new Date(),
                    }
                }
            },
            registerAuthListener: (/*onAuthChange*/) => {
                // call `onAuthChange` to notify the `StorageBrowser` that an end user has signed out
            }
        },
    });

    const defaultValue: StorageBrowserValue = {
        location: {
            bucket: bucket,
            prefix: '',
            permissions: ['delete', 'get', 'list', 'write'],
            path: '',
        },
    };

    return (
        // @ts-ignore
        <StorageBrowser defaultValue={defaultValue} displayText={dictionary} />
    );
};


export const DevelopmentPage = () => {
    const classes = useStyles();
    const {t} = useTranslationRef(pagesTranslationRef);
    const s3Browser = (
        <SideGridItem md={12} header={t('pages.objectStorage')}>
            <CardBody className={classes.body}>
                <S3BrowserComponent />
            </CardBody>
            <SideFooter className={classes.s3footer}>
                <S3KeysLink />
            </SideFooter>
        </SideGridItem>
    );
    return <AreaPage titleKey="pages.development" area="areaDevelopment" side={s3Browser} />;
};
